import { useEffect, useState, FormEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { Eye, EyeOff, Lock, AlertCircle, CheckCircle } from 'lucide-react';
import { supabase } from '../services/supabase';

type Stage = 'loading' | 'ready' | 'no-session' | 'done';

// Pantalla de "nueva contraseña" del flujo de RECUPERACIÓN (web admin).
// El enlace del email de recovery aterriza aquí con `#type=recovery` en el
// hash; supabase-js lo detecta y monta una sesión de recuperación (evento
// PASSWORD_RECOVERY), igual que SetPasswordPage con el invite. Diferencia
// clave: al terminar NO dejamos al usuario dentro del panel — hacemos signOut
// y lo mandamos a /login para que vuelva a pasar el GATE de acceso (rol +
// cuenta activa). Así el reset no puede saltarse el control de acceso.
export default function ResetPasswordPage() {
  const navigate = useNavigate();
  const [stage, setStage] = useState<Stage>('loading');
  const [email, setEmail] = useState<string | null>(null);
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    let cancelled = false;
    const timeout = setTimeout(async () => {
      if (cancelled) return;
      const { data } = await supabase.auth.getSession();
      if (!data.session) setStage('no-session');
    }, 4000);

    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((_event, session) => {
      if (cancelled) return;
      if (session) {
        setEmail(session.user.email ?? null);
        setStage('ready');
        clearTimeout(timeout);
      }
    });

    (async () => {
      const { data } = await supabase.auth.getSession();
      if (cancelled) return;
      if (data.session) {
        setEmail(data.session.user.email ?? null);
        setStage('ready');
        clearTimeout(timeout);
      }
    })();

    return () => {
      cancelled = true;
      clearTimeout(timeout);
      subscription.unsubscribe();
    };
  }, []);

  // Warning transitorio del navigator lock de supabase-js cuando dos contextos
  // compiten por el storage del token; si `getSession()` sigue devolviendo
  // sesión, la operación fue exitosa (mismo patrón que SetPasswordPage).
  function isTransientLockError(message: string): boolean {
    return /lock|released/i.test(message);
  }

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setError('');
    if (password.length < 8) {
      setError('La contraseña debe tener al menos 8 caracteres.');
      return;
    }
    if (password !== confirm) {
      setError('Las contraseñas no coinciden.');
      return;
    }
    setSubmitting(true);
    try {
      const { error: updErr } = await supabase.auth.updateUser({ password });
      if (updErr) {
        if (isTransientLockError(updErr.message)) {
          const { data: probeSess } = await supabase.auth.getSession();
          if (!probeSess.session) {
            throw updErr;
          }
          // sesión vigente → asumimos éxito (el password se persistió)
        } else {
          throw updErr;
        }
      }
      // Cerramos la sesión de recuperación y devolvemos al login: el gate de
      // acceso (rol + activo) se re-aplica al iniciar sesión con la nueva clave.
      await supabase.auth.signOut();
      setStage('done');
      setTimeout(() => navigate('/login', { replace: true }), 1500);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Error al guardar la contraseña.');
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="min-h-screen bg-bg-primary flex items-center justify-center p-4">
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute -top-1/2 -right-1/4 w-96 h-96 rounded-full bg-gold-primary/5 blur-3xl" />
        <div className="absolute -bottom-1/4 -left-1/4 w-96 h-96 rounded-full bg-gold-primary/3 blur-3xl" />
      </div>

      <div className="w-full max-w-md relative">
        <div className="text-center mb-10">
          <img src="/logo.png" alt="VERTEX Performance" className="h-24 mx-auto mb-4" />
          <p className="text-text-secondary text-sm mt-1 font-inter">Restablece tu contraseña</p>
        </div>

        <div className="card shadow-gold">
          {stage === 'loading' && (
            <div className="flex items-center justify-center py-10">
              <div className="w-8 h-8 border-2 border-gold-primary/30 border-t-gold-primary rounded-full animate-spin" />
            </div>
          )}

          {stage === 'no-session' && (
            <div
              className="flex items-start gap-3 bg-error/10 border border-error/30 rounded-lg p-4"
              data-testid="reset-password-no-session"
            >
              <AlertCircle size={18} className="text-error flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-error font-medium text-sm">Enlace no válido o caducado</p>
                <p className="text-text-secondary text-xs mt-2">
                  Vuelve al login y pide de nuevo "¿Olvidaste tu contraseña?". Los enlaces de
                  recuperación caducan tras un tiempo por seguridad.
                </p>
              </div>
            </div>
          )}

          {stage === 'done' && (
            <div
              className="flex items-start gap-3 bg-success/10 border border-success/30 rounded-lg p-4"
              data-testid="reset-password-done"
            >
              <CheckCircle size={18} className="text-success flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-success font-medium text-sm">Contraseña actualizada</p>
                <p className="text-text-secondary text-xs mt-2">
                  Ya puedes iniciar sesión con tu nueva contraseña. Te llevamos al login…
                </p>
              </div>
            </div>
          )}

          {stage === 'ready' && (
            <>
              <h2 className="font-oswald text-xl uppercase tracking-wider text-white mb-2">
                Nueva contraseña
              </h2>
              {email && (
                <p className="text-text-secondary text-sm mb-6 font-mono break-all">{email}</p>
              )}
              <p className="text-text-secondary text-sm mb-6">
                Define una contraseña nueva para tu cuenta del club.
              </p>

              {error && (
                <div className="flex items-center gap-2 bg-error/10 border border-error/30 rounded-lg p-3 mb-4">
                  <AlertCircle size={16} className="text-error flex-shrink-0" />
                  <p className="text-error text-sm font-inter">{error}</p>
                </div>
              )}

              <form onSubmit={handleSubmit} className="space-y-4" data-testid="reset-password-form">
                <div>
                  <label className="label">Contraseña</label>
                  <div className="relative">
                    <Lock
                      size={16}
                      className="absolute left-3 top-1/2 -translate-y-1/2 text-text-secondary"
                    />
                    <input
                      type={showPassword ? 'text' : 'password'}
                      className="input pl-9 pr-10"
                      placeholder="Mínimo 8 caracteres"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      required
                      autoComplete="new-password"
                      minLength={8}
                      data-testid="reset-password-input"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-text-secondary hover:text-text-primary transition-colors"
                    >
                      {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                    </button>
                  </div>
                </div>

                <div>
                  <label className="label">Repite la contraseña</label>
                  <div className="relative">
                    <Lock
                      size={16}
                      className="absolute left-3 top-1/2 -translate-y-1/2 text-text-secondary"
                    />
                    <input
                      type={showPassword ? 'text' : 'password'}
                      className="input pl-9"
                      value={confirm}
                      onChange={(e) => setConfirm(e.target.value)}
                      required
                      autoComplete="new-password"
                      minLength={8}
                      data-testid="reset-password-confirm"
                    />
                  </div>
                </div>

                <button
                  type="submit"
                  disabled={submitting}
                  className="btn-gold w-full mt-2 flex items-center justify-center gap-2"
                  data-testid="reset-password-submit"
                >
                  {submitting ? (
                    <>
                      <span className="w-4 h-4 border-2 border-bg-primary/30 border-t-bg-primary rounded-full animate-spin" />
                      Guardando...
                    </>
                  ) : (
                    'Guardar contraseña'
                  )}
                </button>
              </form>
            </>
          )}
        </div>

        <p className="text-text-secondary text-xs text-center mt-4 font-inter">
          Break your limits!!!
        </p>
      </div>
    </div>
  );
}
